import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { i18n } from '../i18n/i18n';
import {
  authCardClass,
  authErrorBannerClass,
  authInputClass,
  authLabelClass,
  authScreenShellClass,
} from './authUiClasses';
import { resetPassword } from '../services/apiService';

const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = (pwd: string): boolean => {
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasDigit = /\d/.test(pwd);
    return typeof pwd === 'string' && pwd.length >= 12 && hasUpper && hasLower && hasDigit;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) { setError(i18n.t('auth.invalid_token')); return; }
    if (password !== confirm) { setError(i18n.t('auth.password_mismatch')); return; }
    if (!validate(password)) { setError(i18n.t('auth.password_policy_msg')); return; }
    setIsLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (e: any) {
      setError(e?.message || i18n.t('auth.reset_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={authScreenShellClass}>
      <div className={authCardClass}>
        <h2 className="text-center text-gray-800 dark:text-gray-100 mb-8 text-3xl font-semibold">{i18n.t('auth.reset_title')}</h2>
        {success ? (
          <div className="text-center text-gray-700 dark:text-gray-300">
            <p className="mb-4">{i18n.t('auth.reset_success')}</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="mb-5">
              <label htmlFor="password" className={authLabelClass}>
                {i18n.t('auth.new_password_label')}:
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className={authInputClass}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{i18n.t('auth.password_policy_msg')}</p>
            </div>
            <div className="mb-5">
              <label htmlFor="confirm" className={authLabelClass}>
                {i18n.t('auth.confirm_password_label')}:
              </label>
              <input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                disabled={isLoading}
                className={authInputClass}
              />
            </div>
            {error && (
              <div className={authErrorBannerClass}>{error}</div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 py-0 flex items-center justify-center bg-btn-success text-btn-text-primary rounded-lg text-base font-semibold disabled:opacity-70"
            >
              {i18n.t('auth.set_new_password')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;



